import React, { type ReactNode, useState } from 'react';
import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { TASK_MODE } from '../../../../common/task-state/constants';
import { loadAnnotationBitmaps, transformReviewAnnotations } from '../../../labelling/components/annotations/canvas/utils';
import { isSAMSegmentAnnotation } from '../../../labelling/components/annotations/rectangle/bounding-box/helpers';
import type { FrameStateT } from '../../../labelling/types/types';
import type { StageAtoms, StageConfig } from './types/stage-atoms';
import { StageAtomsContext } from './context';
import { resolveAtom } from './utils';
const EMPTY_BITMAPS: Record<string, ImageData> = {};
export function createStageAtoms(config: StageConfig): StageAtoms {
  const annotationStoreAtom = resolveAtom(config.annotationStore);
  const mlAnnotationFramesAtom = resolveAtom(config.mlAnnotationFrames);
  const consensusAnnotationFramesAtom = resolveAtom(config.consensusAnnotationFrames);
  const imageUrlAtom = resolveAtom(config.imageUrl);
  const frameCountAtom = resolveAtom(config.frameCount);
  const currentFrameAtom = resolveAtom(config.currentFrame);
  const annotationUnderUserReviewAtom = resolveAtom(config.annotationUnderUserReview);
  const selectedAnnotationIdsAtom = resolveAtom(config.selectedAnnotationIds);
  const selectedAnnotationLabelAtom = resolveAtom(config.selectedAnnotationLabel);
  const selectedToolAtom = resolveAtom(config.selectedTool);
  const selectedSubToolAtom = resolveAtom(config.selectedSubTool);
  const multiSelectEnabledAtom = resolveAtom(config.multiSelectEnabled);
  const gridModeAtom = resolveAtom(config.gridMode);
  const focusedAnnotationIdAtom = resolveAtom(config.focusedAnnotationId);
  const brushToolSettingsAtom = resolveAtom(config.brushToolSettings);
  const hoveredFrameAtom = resolveAtom(config.hoveredFrame);
  const snapEnabledAtom = resolveAtom(config.snapEnabled);
  const editorModeAtom = resolveAtom(config.editorMode);
  const isLoadingAtom = resolveAtom(config.isLoading);
  const copiedAnnotationIdAtom = resolveAtom(config.copiedAnnotationId);
  const dataUrlAtom = resolveAtom(config.dataUrl);
  const fileTypeAtom = resolveAtom(config.fileType);
  const metadataAtom = resolveAtom(config.metadata);
  const relationshipEditorOpenAtom = resolveAtom(config.relationshipEditorOpen);
  const imageSettingsAtom = resolveAtom(config.imageSettings);
  const taskModeAtom = resolveAtom(config.taskMode);
  const isFocusModeEnabledAtom = resolveAtom(config.isFocusModeEnabled);
  const visibleRelationshipsAtom = resolveAtom(config.visibleRelationships);
  const visibilityModeAtom = resolveAtom(config.visibilityMode);

  // currentFrameDataAtom builds the full FrameStateT for the current frame:
  // - annotationStore from config (real geometry in windowed mode, legacy reducer in memory mode)
  // - annotationMLStore / annotationConsensusStore from their own global atoms (memory mode only)
  const currentFrameDataAtom = atom<FrameStateT>(get => ({
    annotationStore: get(annotationStoreAtom),
    imageUrl: get(imageUrlAtom),
    annotationMLStore: get(mlAnnotationFramesAtom).get(get(currentFrameAtom)),
    annotationConsensusStore: get(consensusAnnotationFramesAtom).get(get(currentFrameAtom))
  }));
  const totalFramesAtom = atom(get => get(frameCountAtom));
  const editableAnnotationAtom = atom(get => {
    const ids = get(selectedAnnotationIdsAtom);
    if (ids.length === 1) {
      return ids[0] ?? null;
    }
    return null;
  });
  const isReviewFocusModeEnabledAtom = atom(get => get(taskModeAtom) === TASK_MODE.REVIEW && get(isFocusModeEnabledAtom));
  const currentFrameAnnotationUnderUserReviewAtom = atom(get => {
    const currentFrame = get(currentFrameAtom);
    const allReview = get(annotationUnderUserReviewAtom);
    return Object.fromEntries(Object.entries(allReview).filter(([, annotation]) => annotation.frameIndex === undefined || annotation.frameIndex === currentFrame));
  });
  const annotationsUnderReviewBitmapsAtom = unwrap(atom(async get => {
    const currentFrameReview = get(currentFrameAnnotationUnderUserReviewAtom);
    const reviewAnnotations = transformReviewAnnotations(currentFrameReview, isSAMSegmentAnnotation);
    const result = await loadAnnotationBitmaps(reviewAnnotations, true);
    return Object.keys(result).length === 0 ? EMPTY_BITMAPS : result;
  }), prev => prev ?? EMPTY_BITMAPS);
  return {
    annotationStoreAtom,
    mlAnnotationFramesAtom,
    consensusAnnotationFramesAtom,
    imageUrlAtom,
    frameCountAtom,
    currentFrameAtom,
    annotationUnderUserReviewAtom,
    selectedAnnotationIdsAtom,
    selectedAnnotationLabelAtom,
    selectedToolAtom,
    selectedSubToolAtom,
    multiSelectEnabledAtom,
    gridModeAtom,
    focusedAnnotationIdAtom,
    brushToolSettingsAtom,
    hoveredFrameAtom,
    snapEnabledAtom,
    editorModeAtom,
    isLoadingAtom,
    copiedAnnotationIdAtom,
    dataUrlAtom,
    fileTypeAtom,
    metadataAtom,
    relationshipEditorOpenAtom,
    imageSettingsAtom,
    taskModeAtom,
    isFocusModeEnabledAtom,
    visibleRelationshipsAtom,
    visibilityModeAtom,
    currentFrameDataAtom,
    totalFramesAtom,
    editableAnnotationAtom,
    isReviewFocusModeEnabledAtom,
    currentFrameAnnotationUnderUserReviewAtom,
    annotationsUnderReviewBitmapsAtom,
    dispatchAtom: config.dispatchAtom
  };
}
type Props = {
  config: StageConfig;
  children: ReactNode;
};
export function StageScope({
  config,
  children
}: Props) {
  // useState ensures atoms are created once and remain stable across re-renders
  const [atoms] = useState(() => createStageAtoms(config));
  return <StageAtomsContext.Provider value={atoms}>{children}</StageAtomsContext.Provider>;
}